// src/queries/account.js
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getAccount, updateProfile, updateFavoriteTeam, changePassword,
  getNotifications, updateNotifications,
  getPrivacyPreferences, updatePrivacyPreferences,
  getComplianceStatus, updateComplianceStatus,
  getPreferences, updatePreferences,
  getSessions, revokeSession, revokeAllOtherSessions,
  startEnable2FA, verify2FA, disable2FA,
  exportMyData, deleteAccount,
} from "@/api/account";
import { getTutorialStatus, resetTutorialStatus, updateNativeTutorialStatus } from "@/api/users";
import { getUser } from "@/api/client";

// ✅ Query Keys som ARRAYER (inte funktioner)!
//    => Devtools visar tydliga namn: ["account"], ["notifications"], ["sessions"]
export const qk = {
  account: ["account"],
  notifications: ["notifications"],
  privacyPreferences: ["privacyPreferences"],
  complianceStatus: ["complianceStatus"],
  preferences: ["accountPreferences"],
  sessions: ["sessions"],
  tutorials: ["tutorialStatus"],
};

// =============== READS ===============

export function useAccount(options = {}) {
  // Hämta bara om vi har en användare i localStorage
  const uid = getUser()?.id;

  return useQuery({
    queryKey: qk.account,
    queryFn: getAccount,
    enabled: !!uid,

    // "Mina matcher"-stil: cacha länge, refetcha bara när vi invalidater
    staleTime: Infinity,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,

    // Visa omedelbart user-info från localStorage medan riktiga svaret laddar
    // (Devtools visar 'stale' tills första nätverks-svaret kommer — helt OK)
    // placeholderData: () => {
    //   const u = getUser();
    //   if (!u) return undefined;
    //   return {
    //     id: u.id,
    //     username: u.username,
    //     display_name: u.username,
    //     email: u.email,
    //     avatar_url: null,
    //     stats: {}, // fylls av servern sen
    //   };
    // },

    ...options,
  });
}

export function useNotifications(options = {}) {
  const uid = getUser()?.id;
  return useQuery({
    queryKey: qk.notifications,
    queryFn: getNotifications,
    enabled: !!uid,

    // Samma cachebeteende som "Mina matcher"
    staleTime: Infinity,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,

    ...options,
  });
}

export function usePrivacyPreferences(options = {}) {
  const uid = getUser()?.id;
  return useQuery({
    queryKey: qk.privacyPreferences,
    queryFn: getPrivacyPreferences,
    enabled: !!uid,
    staleTime: 5 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    ...options,
  });
}

export function useComplianceStatus(options = {}) {
  const uid = getUser()?.id;
  return useQuery({
    queryKey: qk.complianceStatus,
    queryFn: getComplianceStatus,
    enabled: !!uid,
    staleTime: 5 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: true,
    ...options,
  });
}

export function useSessions(options = {}) {
  const uid = getUser()?.id;
  return useQuery({
    queryKey: qk.sessions,
    queryFn: getSessions,
    enabled: !!uid,

    // Om du vill se färska sessioner oftare kan du sätta kortare staleTime
    staleTime: 5 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,

    ...options,
  });
}


export function useTutorialStatus(options = {}) {
  const uid = getUser()?.id;
  return useQuery({
    queryKey: qk.tutorials,
    queryFn: getTutorialStatus,
    enabled: !!uid,
    staleTime: 5 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    ...options,
  });
}

export function useAccountPreferences(options = {}) {
  const uid = getUser()?.id;
  return useQuery({
    queryKey: qk.preferences,
    queryFn: getPreferences,
    enabled: !!uid,
    staleTime: Infinity,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    ...options,
  });
}

// =============== WRITES ===============

export function useUpdatePrivacyPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updatePrivacyPreferences,
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: qk.privacyPreferences });
      const previous = qc.getQueryData(qk.privacyPreferences);
      qc.setQueryData(qk.privacyPreferences, (current) => ({
        ...(current || {}),
        ...(patch?.analytics_enabled !== undefined
          ? { analytics_enabled: patch.analytics_enabled === true, analytics_decided: true }
          : {}),
        ...(patch?.marketing_email_enabled !== undefined
          ? { marketing_email_enabled: patch.marketing_email_enabled === true, marketing_email_decided: true }
          : {}),
      }));
      return { previous };
    },
    onError: (_error, _patch, context) => {
      if (context?.previous) qc.setQueryData(qk.privacyPreferences, context.previous);
    },
    onSuccess: (data) => {
      qc.setQueryData(qk.privacyPreferences, data);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: qk.privacyPreferences });
    },
  });
}

export function useUpdateComplianceStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateComplianceStatus,
    onSuccess: (data) => {
      qc.setQueryData(qk.complianceStatus, data);
      qc.invalidateQueries({ queryKey: qk.privacyPreferences });
    },
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateProfile,
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: qk.account });
      const prev = qc.getQueryData(qk.account);
      qc.setQueryData(qk.account, (old) => ({
        ...(old || {}),
        ...(Object.prototype.hasOwnProperty.call(patch || {}, "display_name")
          ? { display_name: patch.display_name }
          : {}),
        ...(Object.prototype.hasOwnProperty.call(patch || {}, "avatar_url")
          ? { avatar_url: patch.avatar_url }
          : {}),
      }));
      return { prev };
    },
    onError: (_e, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(qk.account, ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: qk.account });
    },
  });
}

export function useUpdateFavoriteTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateFavoriteTeam,
    onMutate: async (favoriteTeamId) => {
      await qc.cancelQueries({ queryKey: qk.account });
      const prev = qc.getQueryData(qk.account);
      qc.setQueryData(qk.account, (old) => ({
        ...(old || {}),
        favorite_team_id: favoriteTeamId || null,
      }));
      return { prev };
    },
    onError: (_error, _favoriteTeamId, context) => {
      if (context?.prev) qc.setQueryData(qk.account, context.prev);
    },
    onSuccess: (response) => {
      qc.setQueryData(qk.account, (old) => ({
        ...(old || {}),
        favorite_team_id: response?.favorite_team_id || null,
      }));
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: qk.account });
    },
  });
}

export function useChangePassword() {
  return useMutation({ mutationFn: changePassword });
}

export function useUpdateNotifications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateNotifications,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.notifications }),
  });
}

export function useUpdateAccountPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updatePreferences,
    onMutate: async (payload) => {
      await qc.cancelQueries({ queryKey: qk.preferences });
      const prev = qc.getQueryData(qk.preferences);
      qc.setQueryData(qk.preferences, payload);
      return { prev };
    },
    onError: (_e, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(qk.preferences, ctx.prev);
    },
    onSuccess: (res) => {
      qc.setQueryData(qk.preferences, res);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: qk.preferences });
    },
  });
}

export function useRevokeSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: revokeSession,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.sessions });
      qc.invalidateQueries({ queryKey: qk.account }); // om du visar sessionsstatistik i kontot
    },
  });
}

export function useRevokeAllOtherSessions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: revokeAllOtherSessions,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.sessions });
      qc.invalidateQueries({ queryKey: qk.account });
    },
  });
}


export function useResetTutorialStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: resetTutorialStatus,
    onSuccess: (res) => {
      qc.setQueryData(qk.tutorials, res);
    },
  });
}

export function useUpdateNativeTutorialStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateNativeTutorialStatus,
    onSuccess: (res) => {
      qc.setQueryData(qk.tutorials, res);
    },
  });
}

export function useStartEnable2FA() {
  return useMutation({ mutationFn: startEnable2FA });
}

export function useVerify2FA() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: verify2FA,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.account }),
  });
}

export function useDisable2FA() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: disable2FA,
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.account }),
  });
}

export function useExportMyData() {
  return useMutation({ mutationFn: exportMyData });
}

export function useDeleteAccount() {
  return useMutation({ mutationFn: deleteAccount });
}

